import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, BarChart3, FileText, Receipt, Shield } from "lucide-react";

const features = [
  { icon: BarChart3, title: "GST Intelligence", desc: "Auto-calculate CGST, SGST & IGST" },
  { icon: FileText, title: "Invoice Generator", desc: "Professional PDF invoices in seconds" },
  { icon: Receipt, title: "Receipt Scanner", desc: "AI-powered expense extraction" },
  { icon: Shield, title: "Tax Compliant", desc: "GST-ready with HSN/SAC codes" },
];

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate(user.home_state ? "/dashboard" : "/onboarding", { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Standalone Google OAuth — redirects directly to our backend which initiates Google consent
    window.location.href = window.location.origin + "/api/auth/google/login";
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 xl:px-24">
        <div className="max-w-md">
          <div className="flex items-center gap-2.5 mb-12">
            <img src="/bizflowa-logo.png" alt="BizFlowa" className="h-10 w-10 rounded-xl object-contain" />
            <span className="font-outfit text-2xl font-bold tracking-tight text-[#0f172a]">BizFlowa</span>
          </div>

          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-[#0f172a] tracking-tight leading-tight mb-4">
            Indian Accounting,
            <br />
            <span className="text-[#4338ca]">Simplified.</span>
          </h1>

          <p className="text-base text-[#64748b] mb-8 leading-relaxed">
            GST-compliant invoicing, smart expense tracking, and AI-powered receipt scanning — all from one command center.
          </p>

          <Button
            onClick={handleLogin}
            data-testid="google-login-btn"
            className="bg-[#4338ca] hover:bg-[#3730a3] text-white h-12 px-8 rounded-xl text-base font-medium shadow-lg shadow-[#4338ca]/25 active:scale-95"
          >
            <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[#f8fafc] items-center justify-center p-12">
        <div className="grid grid-cols-2 gap-5 max-w-lg">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#e2e8f0] hover:-translate-y-1 transition-transform duration-200"
            >
              <div className="h-10 w-10 rounded-lg bg-[#4338ca]/10 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#4338ca]" />
              </div>
              <h3 className="font-outfit font-semibold text-[#0f172a] mb-1">{f.title}</h3>
              <p className="text-sm text-[#64748b]">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer with legal links */}
      <div className="absolute bottom-0 left-0 right-0 py-4 px-8 flex items-center justify-center gap-6 text-xs text-[#94a3b8]">
        <span>Bizflowa Private Limited</span>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#4338ca] transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[#4338ca] transition-colors" data-testid="footer-terms-link">Terms of Use</a>
      </div>
    </div>
  );
}
